import type { DisplayJob } from '../types';
import { getIndexName } from '../../constants';
import { isSummerJobTypeValue, summerJobTypeFacetForLocale } from '../job/summer-job-job-type';
import type { IndexUiState } from '../algolia/url-handling';
import { generateUrlFromIndexUiState } from '../algolia/url-handling';
import { coerceStringArrayField } from './coerce-string-array-field';
import { buildJobDetailContentGuideLinks } from './build-job-detail-content-guide-links';

function createSearchUrl(locale: string, indexUiState: IndexUiState): string {
  const loc = locale === 'fi' || locale === 'sv' ? locale : 'en';
  const indexName = getIndexName(loc);
  const jobsBase = loc === 'fi' ? '/fi/tyopaikat' : loc === 'sv' ? '/sv/lediga-jobb' : '/en/jobs';
  return generateUrlFromIndexUiState(indexUiState, indexName, jobsBase);
}

interface InternalLink {
  title: string;
  url: string;
  priority: 'high' | 'medium' | 'low';
  category: string;
  icon: string;
  description?: string;
}

/**
 * Generates comprehensive internal linking strategy for job pages
 * Creates multiple types of links to improve SEO and user engagement
 */
export function generateInternalLinks(job: DisplayJob, locale: string): InternalLink[] {
  const links: InternalLink[] = [];
  
  // 1. Company-based links (HIGH PRIORITY)
  if (job.companyId) {
    const indexUiState: IndexUiState = {
      refinementList: {
        companyId: [job.companyId]
      }
    };
    const url = createSearchUrl(locale, indexUiState);
    links.push({
      title: `More jobs at ${job.companyId}`,
      url,
      priority: 'high',
      category: 'company',
      icon: '🏢',
      description: 'Explore all open positions at this company'
    });
  }
  
  // 2. Location-based links (HIGH PRIORITY)
  if (job.location && Array.isArray(job.location) && job.location.length > 0) {
    job.location.slice(0, 2).forEach((loc, index) => {
      const indexUiState: IndexUiState = {
        refinementList: {
          location: [loc]
        }
      };
      const url = createSearchUrl(locale, indexUiState);
      links.push({
        title: `Jobs in ${loc}`,
        url,
        priority: 'high',
        category: 'location',
        icon: '📍',
        description: `Find jobs in ${loc}`
      });
    });
  }
  
  // 3. Industry-based links (MEDIUM PRIORITY)
  if (job.companyIndustry) {
    const indexUiState: IndexUiState = {
      refinementList: {
        companyIndustry: [job.companyIndustry]
      }
    };
    const url = createSearchUrl(locale, indexUiState);
    links.push({
      title: `${job.companyIndustry} jobs`,
      url,
      priority: 'medium',
      category: 'industry',
      icon: '🏭',
      description: `Explore ${job.companyIndustry} industry opportunities`
    });
  }
  
  // 4. Role level links (MEDIUM PRIORITY)
  if (job.normalisedRoleLevel) {
    const indexUiState: IndexUiState = {
      refinementList: {
        roleLevel: [job.normalisedRoleLevel]
      }
    };
    const url = createSearchUrl(locale, indexUiState);
    links.push({
      title: `${job.normalisedRoleLevel} jobs`,
      url,
      priority: 'medium',
      category: 'level',
      icon: '📊',
      description: `Find ${job.normalisedRoleLevel} level positions`
    });
  }
  
  // 5. Remote work links (MEDIUM PRIORITY)
  if (job.remoteness && job.remoteness.toLowerCase().includes('remote')) {
    const indexUiState: IndexUiState = {
      refinementList: {
        remoteness: ['Remote']
      }
    };
    const url = createSearchUrl(locale, indexUiState);
    links.push({
      title: 'Remote jobs',
      url,
      priority: 'medium',
      category: 'remote',
      icon: '🏠',
      description: 'Find remote work opportunities'
    });
  }
  
  // 6. Skills/Technologies links (LOW PRIORITY)
  const skills = coerceStringArrayField(job.skillsAndTechnologies);
  if (skills.length > 0) {
    skills.slice(0, 2).forEach((skill) => {
      const indexUiState: IndexUiState = {
        query: skill
      };
      const url = createSearchUrl(locale, indexUiState);
      links.push({
        title: `Jobs requiring ${skill}`,
        url,
        priority: 'low',
        category: 'skills',
        icon: '💻',
        description: `Find jobs requiring ${skill}`
      });
    });
  }
  
  // 7. Job type links (MEDIUM PRIORITY)
  if (job.jobType) {
    const indexUiState: IndexUiState = {
      refinementList: {
        jobType: [job.jobType]
      }
    };
    const url = createSearchUrl(locale, indexUiState);
    links.push({
      title: `${job.jobType} jobs`,
      url,
      priority: 'medium',
      category: 'type',
      icon: '📋',
      description: `Find ${job.jobType} positions`
    });
  }
  
  // 8. Language requirement links (LOW PRIORITY)
  const requiredLanguages = coerceStringArrayField(job.requiredLanguages);
  if (requiredLanguages.length > 0) {
    requiredLanguages.slice(0, 1).forEach((lang) => {
      const indexUiState: IndexUiState = {
        refinementList: {
          requiredLanguages: [lang]
        }
      };
      const url = createSearchUrl(locale, indexUiState);
      links.push({
        title: `Jobs requiring ${lang}`,
        url,
        priority: 'low',
        category: 'languages',
        icon: '🌍',
        description: `Find jobs requiring ${lang}`
      });
    });
  }
  
  // 9. Salary range links (LOW PRIORITY)
  if (job.salaryRangeMin && job.salaryRangeMax) {
    const avgSalary = Math.round((job.salaryRangeMin + job.salaryRangeMax) / 2);
    const minSalary = Math.round(avgSalary * 0.8);
    const maxSalary = Math.round(avgSalary * 1.2);
    const indexUiState: IndexUiState = {
      refinementList: {
        salaryRangeMin: [minSalary.toString()],
        salaryRangeMax: [maxSalary.toString()]
      }
    };
    const url = createSearchUrl(locale, indexUiState);
    links.push({
      title: `Jobs with similar salary`,
      url,
      priority: 'low',
      category: 'salary',
      icon: '💰',
      description: 'Find jobs with similar salary ranges'
    });
  }
  
  // 10. Field/Department links (MEDIUM PRIORITY)
  if (job.field) {
    const indexUiState: IndexUiState = {
      refinementList: {
        field: [job.field]
      }
    };
    const url = createSearchUrl(locale, indexUiState);
    links.push({
      title: `${job.field} jobs`,
      url,
      priority: 'medium',
      category: 'field',
      icon: '🎯',
      description: `Find ${job.field} positions`
    });
  }
  
  return links;
}

/**
 * Generates contextual internal links based on job characteristics
 */
export function generateContextualLinks(job: DisplayJob, locale: string): InternalLink[] {
  const links: InternalLink[] = [];
  
  // Special job types
  if (isSummerJobTypeValue(job.jobType)) {
    const loc = locale === 'fi' || locale === 'sv' ? locale : 'en';
    const indexUiState: IndexUiState = {
      refinementList: {
        jobType: [summerJobTypeFacetForLocale(loc)],
      },
    };
    const url = createSearchUrl(locale, indexUiState);
    links.push({
      title: 'Summer jobs',
      url,
      priority: 'medium',
      category: 'special',
      icon: '☀️',
      description: 'Find summer job opportunities'
    });
  }
  
  if (job.isOpenApplication) {
    const indexUiState: IndexUiState = {
      refinementList: {
        isOpenApplication: ['true']
      }
    };
    const url = createSearchUrl(locale, indexUiState);
    links.push({
      title: 'Open applications',
      url,
      priority: 'medium',
      category: 'special',
      icon: '📝',
      description: 'Find open application positions'
    });
  }
  
  if (job.isDriversLicenseRequired) {
    const indexUiState: IndexUiState = {
      refinementList: {
        isDriversLicenseRequired: ['true']
      }
    };
    const url = createSearchUrl(locale, indexUiState);
    links.push({
      title: 'Jobs requiring driver\'s license',
      url,
      priority: 'low',
      category: 'special',
      icon: '🚗',
      description: 'Find jobs requiring driver\'s license'
    });
  }
  
  return links;
}

/**
 * Generates related content links for blog posts and guides
 */
export function generateRelatedContentLinks(job: DisplayJob, locale: string): InternalLink[] {
  return buildJobDetailContentGuideLinks(job, locale);
} 